/**
 * Identify the audio container from the first 16 bytes of the blob.
 * The stored mime_type in the DB is sometimes wrong (cross-platform uploads,
 * legacy octet-stream rows). The magic header is authoritative.
 *
 * For ISO BMFF (MP4) we also pull the major brand at offset 8 — useful for
 * diagnosing decoder failures (fragmented MP4 from Safari/MediaRecorder
 * advertises brands like `iso5` / `mp42` and Firefox can refuse audio-only
 * fragmented MP4 even though the file is technically valid).
 */
export type SniffResult = {
  mime: string | null;
  brand: string | null;
  /** FourCC of the audio codec inside the container, when we can find it. */
  codec: string | null;
};

const _UNPLAYABLE_CODECS: Record<string, string> = {
  samr: "AMR-NB",
  sawb: "AMR-WB",
  ".mp1": "MPEG audio Layer 1",
  ".mp2": "MPEG audio Layer 2",
};

export function describeUnplayableCodec(codec: string | null): string | null {
  if (!codec) return null;
  return _UNPLAYABLE_CODECS[codec.toLowerCase()] ?? null;
}

function indexOfAscii(haystack: Uint8Array, needle: string): number {
  outer: for (let i = 0; i <= haystack.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (haystack[i + j] !== needle.charCodeAt(j)) continue outer;
    }
    return i;
  }
  return -1;
}

export async function sniffAudio(blob: Blob): Promise<SniffResult> {
  const head = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
  const at = (offset: number, ...bytes: number[]) =>
    bytes.every((b, i) => head[offset + i] === b);
  if (at(0, 0x1a, 0x45, 0xdf, 0xa3)) return { mime: "audio/webm", brand: null, codec: null };
  if (at(4, 0x66, 0x74, 0x79, 0x70)) {
    const brand = String.fromCharCode(head[8]!, head[9]!, head[10]!, head[11]!).trim();
    let codec: string | null = null;
    try {
      const probe = new Uint8Array(
        await blob.slice(0, Math.min(blob.size, 4096)).arrayBuffer(),
      );
      for (const cc of ["mp4a", "samr", "sawb", "opus", "alac", "flac", ".mp3"]) {
        const idx = indexOfAscii(probe, cc);
        if (idx > 0) {
          codec = cc;
          break;
        }
      }
    } catch {
      /* ignore */
    }
    return { mime: "audio/mp4", brand, codec };
  }
  if (at(0, 0x4f, 0x67, 0x67, 0x53)) return { mime: "audio/ogg", brand: null, codec: null };
  if (at(0, 0x52, 0x49, 0x46, 0x46) && at(8, 0x57, 0x41, 0x56, 0x45)) {
    return { mime: "audio/wav", brand: null, codec: null };
  }
  if (at(0, 0x66, 0x4c, 0x61, 0x43)) return { mime: "audio/flac", brand: null, codec: null };
  if (at(0, 0x49, 0x44, 0x33)) return { mime: "audio/mpeg", brand: null, codec: null };
  if (head[0] === 0xff && (head[1] & 0xe0) === 0xe0) {
    return { mime: "audio/mpeg", brand: null, codec: null };
  }
  return { mime: null, brand: null, codec: null };
}

export function isGenericMime(mime: string): boolean {
  const base = (mime || "").split(";")[0]?.trim().toLowerCase() ?? "";
  return !base || base === "application/octet-stream" || base === "binary/octet-stream";
}
